import { Injectable } from '@angular/core';

import { DatehandlerService } from './datehandler.service';

import {
   DateTokens,
   TimePeriods,
   RelativeTokens,
} from './datehandler.model';

export type Operations = '+' | '-' | '~';

export interface OperandToken {
   token: string;
}

export interface OperandRelative {
   operation: Operations;
   amount: number;
   period: string;
}

export interface ParsedDateString {
   start: string;
   operands: ( OperandToken | OperandRelative )[];
}

@Injectable({
   providedIn: 'root'
})
export class RelativeDateService {

   constructor( private dh: DatehandlerService ) { }

   parseString( dateString: string, relativeDates?: {[ key: string ]: string }): ParsedDateString {
      if ( !dateString ) { return; }
      if ( new Date( dateString ).toString() !== 'Invalid Date' ) { return; }

      let start;
      const operands = [];

      const dateTokens = ( dates: {[ key: string ]: string } = DateTokens ) => Object.keys( dates ).map( key => dates[key] );

      start = dateTokens( relativeDates ).filter( token => dateString.indexOf( token ) === 0 ).sort(( a, b ) => b.length - a.length )[0];

      if ( !start ) { return; }
      dateString = dateString.slice( start.length );

      // find relative operations
      let regexString = '';

      regexString += '((?:[+-][1-9][0-9]{0,2}[' + TimePeriods.join( '' ) + '])|';

      const relative = Object.keys( RelativeTokens ).map( key => RelativeTokens[key] );
      regexString += '(?:[~](?:' + relative.join( '|' ).replace( /([+-])/gi, '\\$1' ) + ')))';

      // grab matches and loop over
      dateString.split( new RegExp( regexString, 'gi' )).map( match => {
         if ( !match.trim().length ) { return; }

         const check = match.slice( 0, 1 );

         switch ( check ) {
            case '~':
               operands.push({ token: match.slice( 1 ) });
               break;
            case '+':
            case '-':
               operands.push({
                  operation: match.slice( 0, 1 ),
                  amount: match.slice( 1, match.length - 1 ),
                  period: match.slice( match.length - 1 )
               });
               break;
         }
      });

      return { start, operands };
   }

   makeString( date: ParsedDateString ): string {
      if ( !date?.start ) { return; }

      return date.start + date.operands.map(( o: any ) =>
         o?.operation === '~' ? `~${o.token}` : `${o.operation}${o.amount}${o.period}`
      ).join('');
   }

   getDate( date: string | ParsedDateString ): string {
      if ( typeof date !== 'string' ) {
         date = this.makeString( date );
      }

      this.dh.outputFormat = 'YYYY-MM-DD';
      const ret = this.dh.normalizeDate( date );

      return ret ? ret.toString() : new Date().toString();
   }

   getIsoDate( date: string | ParsedDateString ): string {
      if ( typeof date !== 'string' ) {
         date = this.makeString( date );
      }

      if ( !this.dh.normalizeDate( date )) { return; }

      return this.dh.dateTimeObj.startOf( 'day' ).format();
   }
}
